import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { MapPin, Pencil, Plus, Trash2, Hammer } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { WorkplaceDialog } from "@/components/app/WorkplaceDialog";
import { OFFSITE, GeofenceControls } from "@/components/app/GeofenceControls";
import { InvitePanel } from "@/components/app/InvitePanel";
import { MembersTable, ROLE_INFO } from "@/components/app/MembersTable";
import { BusinessForm, TimezoneForm } from "@/components/app/BusinessForms";
import { PayrollForm } from "@/components/app/PayrollForm";
import { RemindersForm } from "@/components/app/RemindersForm";
import { PlanPicker } from "@/components/app/PlanPicker";
import { LinkedSettings } from "@/components/app/LinkedBusiness";
import { useWorkplaces } from "@/pages/app/onboarding/WorkplaceSteps";
import { PageHeader, NAV } from "@/pages/app/AppLayout";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export const Workplaces = () => {
  const { business } = useAuth();
  const [list, load] = useWorkplaces();
  const [editing, setEditing] = useState(undefined);
  const [archive, setArchive] = useState(null);
  const doArchive = async () => {
    try { await api.delete(`/workplaces/${archive.workplace_id}`); toast.success("Workplace archived"); load(); } catch (err) { toast.error(formatError(err)); }
    setArchive(null);
  };
  return (
    <div data-testid="workplaces-page">
      <PageHeader eyebrow="Geofences" title="Workplaces & geofences">
        <Button onClick={() => setEditing(null)} className="rounded-full bg-[#00A85C] hover:bg-[#008F4F]" data-testid="add-workplace-btn"><Plus className="mr-1 h-4 w-4" />Add workplace</Button>
      </PageHeader>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {(list || []).map((w) => (
          <div key={w.workplace_id} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`workplace-card-${w.workplace_id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0"><p className="font-semibold">{w.name}</p><p className="truncate text-xs text-slate-500">{w.address || "No address"}</p></div>
              <div className="flex"><Button size="icon" variant="ghost" onClick={() => setEditing(w)} aria-label="Edit" data-testid={`workplace-edit-${w.workplace_id}`}><Pencil className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => setArchive(w)} aria-label="Archive" data-testid={`workplace-archive-${w.workplace_id}`}><Trash2 className="h-4 w-4 text-red-600" /></Button></div>
            </div>
            <div className="mt-4 flex flex-wrap gap-2 text-xs">
              <span className={`rounded-full px-2.5 py-0.5 font-semibold ${w.lat != null ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-800"}`}><MapPin className="mr-1 inline h-3 w-3" />{w.lat != null ? "Pinned" : "Not pinned"}</span>
              <span className="rounded-full bg-slate-100 px-2.5 py-0.5 font-semibold text-slate-700">{w.geofence_radius_m} m</span>
              <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-slate-600">{w.location_verification ? "Location verified" : "No verification"}</span>
              <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-slate-600">{OFFSITE[w.offsite_policy]}</span>
            </div>
          </div>
        ))}
        {list?.length === 0 && <p className="text-sm text-slate-500" data-testid="workplaces-empty">No workplaces yet. Add your first one.</p>}
      </div>
      {editing !== undefined && <WorkplaceDialog key={editing?.workplace_id || "new"} open onOpenChange={(o) => !o && setEditing(undefined)} workplace={editing} policy={business?.settings.clock_policy} onSaved={load} />}
      <AlertDialog open={!!archive} onOpenChange={(o) => !o && setArchive(null)}>
        <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Archive {archive?.name}?</AlertDialogTitle><AlertDialogDescription>Staff won't be able to clock in here. History is kept.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel data-testid="archive-cancel-btn">Cancel</AlertDialogCancel><AlertDialogAction onClick={doArchive} className="bg-red-600 hover:bg-red-700" data-testid="archive-confirm-btn">Archive</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export const Employees = () => (
  <div data-testid="employees-page">
    <PageHeader eyebrow="Team" title="Employees" />
    <Tabs defaultValue="members">
      <TabsList><TabsTrigger value="members" data-testid="tab-members">Team members</TabsTrigger><TabsTrigger value="invite" data-testid="tab-invite">Invite</TabsTrigger><TabsTrigger value="roles" data-testid="tab-roles">Roles</TabsTrigger></TabsList>
      <TabsContent value="members" className="mt-5"><MembersTable /></TabsContent>
      <TabsContent value="invite" className="mt-5"><InvitePanel /></TabsContent>
      <TabsContent value="roles" className="mt-5"><div className="grid gap-3 sm:grid-cols-3">{Object.entries(ROLE_INFO).map(([r, d]) => <div key={r} className="rounded-xl border border-slate-200 bg-white p-4"><p className="font-semibold capitalize">{r}</p><p className="mt-1 text-sm text-slate-500">{d}</p></div>)}</div></TabsContent>
    </Tabs>
  </div>
);

const ClockPolicyForm = ({ business, onSaved }) => {
  const p = business.settings.clock_policy;
  const [v, setV] = useState({ geofence_radius_m: p.default_radius_m, location_verification: p.require_location, offsite_policy: p.offsite_policy });
  const save = async () => {
    try {
      const { data } = await api.patch("/business/settings", { clock_policy: { default_radius_m: v.geofence_radius_m, require_location: v.location_verification, offsite_policy: v.offsite_policy } });
      toast.success("Default clock-in policy saved");
      onSaved(data);
    } catch (err) { toast.error(formatError(err)); }
  };
  return <div className="max-w-lg space-y-5"><p className="text-sm text-slate-500">Defaults for new workplaces. Each workplace can override these.</p><GeofenceControls value={v} onChange={(x) => setV({ ...v, ...x })} idPrefix="policy" /><Button onClick={save} className="rounded-full bg-[#00A85C] hover:bg-[#008F4F]" data-testid="policy-save-btn">Save defaults</Button></div>;
};

const AuditLog = () => {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get("/audit-log").then((r) => setRows(r.data)).catch(() => setRows([]));
  }, []);
  return (
    <ul className="divide-y divide-slate-100 rounded-2xl border border-slate-200 bg-white" data-testid="audit-log">
      {(rows || []).map((r) => <li key={r.audit_id} className="flex justify-between gap-3 px-4 py-2.5 text-sm"><span className="font-mono-num text-xs">{r.action}</span><span className="text-xs text-slate-500">{new Date(r.created_at).toLocaleString("en-AU")}</span></li>)}
      {rows?.length === 0 && <li className="px-4 py-3 text-sm text-slate-500">No activity yet.</li>}
    </ul>
  );
};

export const SettingsPage = () => {
  const { business, loadBusiness } = useAuth();
  if (!business) return null;
  const card = (c) => <div className="rounded-2xl border border-slate-200 bg-white p-6">{c}</div>;
  return (
    <div data-testid="settings-page">
      <PageHeader eyebrow="Settings" title="Business settings" />
      <Tabs defaultValue="business">
        <TabsList className="h-auto flex-wrap"><TabsTrigger value="business" data-testid="tab-business">Business</TabsTrigger><TabsTrigger value="linked" data-testid="tab-linked">Linked businesses</TabsTrigger><TabsTrigger value="time" data-testid="tab-time">Time zone</TabsTrigger><TabsTrigger value="payroll" data-testid="tab-payroll">Breaks & payroll</TabsTrigger><TabsTrigger value="policy" data-testid="tab-policy">Clock-in policy</TabsTrigger><TabsTrigger value="reminders" data-testid="tab-reminders">Reminders</TabsTrigger><TabsTrigger value="audit" data-testid="tab-audit">Audit log</TabsTrigger></TabsList>
        <TabsContent value="business" className="mt-5">{card(<BusinessForm key={business.business_id} business={business} onSaved={loadBusiness} />)}</TabsContent>
        <TabsContent value="linked" className="mt-5"><LinkedSettings /></TabsContent>
        <TabsContent value="time" className="mt-5">{card(<TimezoneForm key={business.business_id} business={business} onSaved={loadBusiness} />)}</TabsContent>
        <TabsContent value="payroll" className="mt-5">{card(<PayrollForm key={business.business_id} business={business} onSaved={loadBusiness} />)}</TabsContent>
        <TabsContent value="policy" className="mt-5">{card(<ClockPolicyForm key={business.business_id} business={business} onSaved={loadBusiness} />)}</TabsContent>
        <TabsContent value="reminders" className="mt-5">{card(<RemindersForm key={business.business_id} business={business} onSaved={loadBusiness} />)}</TabsContent>
        <TabsContent value="audit" className="mt-5"><AuditLog /></TabsContent>
      </Tabs>
    </div>
  );
};

export const Subscription = () => {
  const { business, loadBusiness } = useAuth();
  if (!business) return null;
  const sub = business.subscription || {};
  return (
    <div data-testid="subscription-page">
      <PageHeader eyebrow="Subscription" title="Plan & billing" />
      <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-6" data-testid="subscription-status">
        <p className="text-sm text-slate-500">Current plan</p>
        <p className="font-display text-xl font-bold">{sub.plan_name || "No plan selected"}</p>
        {sub.status === "trialing" && <p className="mt-1 text-sm text-[#00A85C]">Free trial until {new Date(sub.trial_ends_at).toLocaleDateString("en-AU")}</p>}
      </div>
      <PlanPicker business={business} onSaved={loadBusiness} />
    </div>
  );
};

export const Account = () => {
  const { user, refresh } = useAuth();
  const [name, setName] = useState(user.name);
  const save = async () => {
    try { await api.patch("/auth/me", { name }); await refresh(); toast.success("Profile updated"); } catch (err) { toast.error(formatError(err)); }
  };
  return (
    <div className="max-w-2xl space-y-6" data-testid="account-page">
      <PageHeader eyebrow="Account" title="Account & security" />
      <div className="space-y-3 rounded-2xl border border-slate-200 bg-white p-5">
        <div className="space-y-1.5"><Label>Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="h-11 rounded-xl" data-testid="account-name-input" /></div>
        <p className="text-sm text-slate-500">{user.email}</p>
        <Button onClick={save} className="rounded-full bg-[#00A85C] hover:bg-[#008F4F]" data-testid="account-save-btn">Save</Button>
      </div>
    </div>
  );
};

export const ComingSoon = () => {
  const { pathname } = useLocation();
  const item = NAV.find((n) => n[2] && pathname.endsWith(`/${n[2]}`));
  return (
    <div data-testid="coming-soon-page">
      <PageHeader eyebrow="Coming next" title={item ? item[1] : "Coming soon"} />
      <div className="flex items-start gap-4 rounded-2xl border border-dashed border-slate-300 bg-white p-8">
        <Hammer className="h-6 w-6 text-[#E07800]" />
        <div><p className="font-semibold">This module is being built in the next Phase 1 stage.</p><p className="mt-1 text-sm text-slate-500">Your business setup, workplaces and team are ready for it.</p></div>
      </div>
    </div>
  );
};
